/**
 * Copyright (c) 2016 Konstantin Kulinicenko.
 * Licensed under the MIT License (MIT), see
 * https://github.com/40818419/react-code-input
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
// import './code-input.css';
import { Decorator } from '../decorator';
var BACKSPACE_KEY = 8;
var LEFT_ARROW_KEY = 37;
var UP_ARROW_KEY = 38;
var RIGHT_ARROW_KEY = 39;
var DOWN_ARROW_KEY = 40;
var E_KEY = 69;
export var CodeInput = function (_a) {
    var _b = _a.autoComplete, autoComplete = _b === void 0 ? 'off' : _b, _c = _a.autoFocus, autoFocus = _c === void 0 ? true : _c, _d = _a.disabled, disabled = _d === void 0 ? false : _d, _e = _a.forceUppercase, forceUppercase = _e === void 0 ? false : _e, _f = _a.fields, fields = _f === void 0 ? 4 : _f, valueProp = _a.value, _g = _a.type, type = _g === void 0 ? 'number' : _g, _h = _a.filterKeyCodes, filterKeyCodes = _h === void 0 ? [189, 190] : _h, _j = _a.filterChars, filterChars = _j === void 0 ? ['-', '.'] : _j, _k = _a.filterCharsIsWhitelist, filterCharsIsWhitelist = _k === void 0 ? false : _k, _l = _a.inputClassName, inputClassName = _l === void 0 ? "" : _l, _m = _a.inputClassNameInvalid, inputClassNameInvalid = _m === void 0 ? "" : _m, pattern = _a.pattern, _o = _a.inputMode, inputMode = _o === void 0 ? "numeric" : _o, placeholder = _a.placeholder, touch = _a.touch, untouch = _a.untouch, name = _a.name, onChange = _a.onChange, hint = _a.hint, label = _a.label, error = _a.error, errorMessage = _a.errorMessage, id = _a.id, fieldError = _a.fieldError, requiredIndicator = _a.requiredIndicator, className = _a.className;
    var _p = useState(valueProp !== null && valueProp !== void 0 ? valueProp : ""), value = _p[0], setValue = _p[1];
    var _q = useState([]), input = _q[0], setInput = _q[1];
    var textInput = useRef([]);
    var isValid = !(error || errorMessage || fieldError);
    useEffect(function () {
        if (value !== valueProp) {
            if (typeof valueProp === "string") {
                setValue(valueProp);
            }
            else if (typeof valueProp === "number") {
                setValue("".concat(valueProp));
            }
            else if (valueProp === null || valueProp === undefined) {
                setValue("");
            }
        }
    }, [value, valueProp]);
    useEffect(function () {
        setInput(Array(fields).fill("").map(function (_, i) { var _a; return (_a = value[i]) !== null && _a !== void 0 ? _a : ""; }));
    }, [fields, value]);
    var handleTouch = useCallback(function (value) {
        if (typeof touch === 'function' && typeof untouch === 'function') {
            if (value === '') {
                touch(name);
            }
            else {
                untouch(name);
            }
        }
    }, [name, touch, untouch]);
    var handleBlur = useCallback(function (e) {
        handleTouch(e.target.value);
    }, [handleTouch]);
    var handleChange = useCallback(function (e) {
        var value = String(e.target.value);
        if (forceUppercase) {
            value = value.toUpperCase();
        }
        if (type === 'number') {
            value = value.replace(/[^\d]/g, '');
        }
        /** Filter Chars */
        value = value.split('').filter(function (currChar) {
            if (filterCharsIsWhitelist) {
                return filterChars.includes(currChar);
            }
            return !filterChars.includes(currChar);
        }).join('');
        var fullValue = value;
        if (value !== '') {
            var thisInput_1 = input.slice();
            if (value.length > 1) {
                value.split('').map(function (chart, i) {
                    if (Number(e.target.dataset.id) + i < fields) {
                        thisInput_1[Number(e.target.dataset.id) + i] = chart;
                    }
                    return false;
                });
            }
            else {
                thisInput_1[Number(e.target.dataset.id)] = value;
            }
            thisInput_1.map(function (s, i) {
                if (textInput.current[i]) {
                    textInput.current[i].value = s;
                }
                return false;
            });
            var newTarget = textInput.current[e.target.dataset.id < thisInput_1.length
                ? Number(e.target.dataset.id) + 1
                : e.target.dataset.id];
            if (newTarget) {
                newTarget.focus();
                newTarget.select();
            }
            fullValue = thisInput_1.join('');
            setValue(fullValue);
        }
        if (onChange && fullValue) {
            onChange(fullValue);
        }
        handleTouch(fullValue);
    }, [fields, filterChars, filterCharsIsWhitelist, forceUppercase, handleTouch, input, onChange, type]);
    var handleKeyDown = useCallback(function (e) {
        var target = Number(e.target.dataset.id), nextTarget = textInput.current[target + 1], prevTarget = textInput.current[target - 1];
        var thisInput, value;
        if (filterKeyCodes.length > 0) {
            filterKeyCodes.map(function (item) {
                if (item === e.keyCode) {
                    e.preventDefault();
                    return true;
                }
            });
        }
        switch (e.keyCode) {
            case BACKSPACE_KEY:
                e.preventDefault();
                textInput.current[target].value = '';
                thisInput = input.slice();
                thisInput[target] = '';
                value = thisInput.join('');
                setValue(value);
                if (textInput.current[target].value === '') {
                    if (prevTarget) {
                        prevTarget.focus();
                        prevTarget.select();
                    }
                }
                if (onChange) {
                    onChange(value);
                }
                break;
            case LEFT_ARROW_KEY:
                e.preventDefault();
                if (prevTarget) {
                    prevTarget.focus();
                    prevTarget.select();
                }
                break;
            case RIGHT_ARROW_KEY:
                e.preventDefault();
                if (nextTarget) {
                    nextTarget.focus();
                    nextTarget.select();
                }
                break;
            case UP_ARROW_KEY:
                e.preventDefault();
                break;
            case DOWN_ARROW_KEY:
                e.preventDefault();
                break;
            case E_KEY: // This case needs to be handled because of https://stackoverflow.com/questions/31706611/why-does-the-html-input-with-type-number-allow-the-letter-e-to-be-entered-in
                if (e.target.type === 'number') {
                    e.preventDefault();
                    break;
                }
                break;
            default:
                break;
        }
        handleTouch(value);
    }, [filterKeyCodes, handleTouch, input, onChange]);
    return (React.createElement(Decorator, { id: id, className: className, error: error, errorMessage: errorMessage, label: label, hint: hint, fieldError: fieldError, requiredIndicator: requiredIndicator },
        React.createElement("fieldset", { className: classNames(className, 'react-code-input flex flex-row gap-1 sm:gap-2 justify-center items-center'), id: id }, input.map(function (value, i) {
            var _a;
            return (React.createElement(React.Fragment, { key: "input_".concat(i) },
                React.createElement("input", { ref: function (ref) {
                        textInput.current[i] = ref;
                    }, id: id ? "".concat(id, "-").concat(i) : undefined, "data-id": i, autoFocus: autoFocus && (i === 0) ? true : false, value: value, type: type, min: 0, max: 9, maxLength: input.length === i + 1 ? 1 : input.length, autoComplete: autoComplete, onFocus: function (e) { return e.target.select(); }, className: classNames('input border-none ring-inset focus:shadow-md focus:shadow-primary-100 focus:ring-primary text-center min-w-0 w-lh h-[1.07lh] rounded-lg border-gray-300 leading-tight p-0 font-semibold text-primary placeholder:text-gray-300 disabled:bg-gray-50 disabled:text-gray-500', inputClassName, (_a = {},
                        _a[inputClassNameInvalid] = !isValid,
                        _a["invalid"] = !isValid,
                        _a)), onBlur: handleBlur, onChange: handleChange, onKeyDown: handleKeyDown, disabled: disabled, "data-valid": isValid, pattern: pattern, inputMode: inputMode, placeholder: placeholder === null || placeholder === void 0 ? void 0 : placeholder[i], required: true }),
                fields > 4 && fields % 2 === 0 && ~~(fields / 2) - 1 === i && React.createElement("span", { className: "text-gray-300" }, "-")));
        }))));
};
